import { LogOut } from 'lucide-react';
import { Outlet, useLocation } from 'react-router-dom';
import { toast } from '@/components/ui/helpers/toast';
import { useShallow } from 'zustand/react/shallow';

import { AppTopbar } from '@/components/layout/app-topbar';
import { Button } from '@/components/ui/button';
import { useLogout } from '@/features/auth/hooks/use-logout';
import { getApiErrorMessage } from '@/lib/helpers/api-errors/get-api-error-message';
import { useAuthToken } from '@/contexts/auth/auth-context';
import { useSignupFlowStore } from '@/features/signup/stores/use-signup-flow-store';

const AuthHeader = () => {
  const location = useLocation();
  const token = useAuthToken();
  const { completedStep, email } = useSignupFlowStore(
    useShallow(s => ({ completedStep: s.completedStep, email: s.email }))
  );

  const logout = useLogout({
    onError: (error) => {
      toast.error(getApiErrorMessage(error));
    },
  });

  const exceptions = [
    '/shopify/subscription-complete',
    '/shopify/subscription-plans/',
    '/shopify/login/success',
    '/v2/shopify/sign-up/',
    '/v2/shopify/otp/',
    '/v2/shopify/otp',
    '/v2/login',
    '/v2/login/',
  ];

  const isSignupFlowReset = completedStep === 0 && email === '';
  const legacyBillingPages = ['/payment', '/signup-existing-user'];
  const signupFlowV2BillingPages = ['/signup/plans', '/signup/payment'];

  const isOnLegacyBillingPage = legacyBillingPages.includes(location.pathname);
  const isOnSignupFlowV2BillingPage =
    signupFlowV2BillingPages.includes(location.pathname) && isSignupFlowReset;

  const showLogoutButton = token && (isOnLegacyBillingPage || isOnSignupFlowV2BillingPage);

  const isInAuthenticatedSignupFlow = completedStep >= 2;
  const isOnSignupFlowPage =
    location.pathname.startsWith('/signup/') &&
    !['/signup', '/signup/otp'].includes(location.pathname);
  const shouldHideHeader = token && isInAuthenticatedSignupFlow && isOnSignupFlowPage;

  const signOut = async (): Promise<void> => {
    try {
      await logout();
    } catch {
      return;
    }
  };

  const handleSignOut = () => {
    void signOut();
  };

  return (
    <div className="font-activation">
      {!shouldHideHeader && (
        <AppTopbar
          logoLinked={!exceptions.includes(location.pathname)}
          actions={
            showLogoutButton ? (
              <Button
                variant="outline"
                size="sm"
                onClick={handleSignOut}
                className="flex items-center gap-2"
              >
                <LogOut className="h-4 w-4" />
                <span>Logout</span>
              </Button>
            ) : undefined
          }
        />
      )}
      <Outlet />
    </div>
  );
};

export default AuthHeader;
